import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import Frame from "@/components/Frame";
import DevisForm from "@/components/DevisForm";
import { linkify } from "@/components/Linkify";
import { FadeIn, MaskedLine, MaskedLineScroll, Overline } from "@/components/Reveal";
import { seoPages } from "@/data/seoPages";

export default function SeoPage({ page }) {
  const others = seoPages.filter((p) => p.slug !== page.slug).slice(0, 3);
  const currentPath = `/${page.slug}`;
  const usedLinks = new Set();
  const crumbs = [
    { label: "Accueil", to: "/" },
    ...(page.slug === "couvreur-94" ? [] : [{ label: "Couvreur 94", to: "/couvreur-94" }]),
    { label: page.footerLabel },
  ];
  return (
    <>
      <Seo title={page.metaTitle} description={page.metaDescription} path={`/${page.slug}`} />
      <Breadcrumb items={crumbs} />
      <section className="bg-midnight text-white grain relative overflow-hidden">
        <div className="absolute inset-0 blueprint-dark opacity-60" aria-hidden="true" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 pt-20 pb-16 md:pt-28 md:pb-20">
          <MaskedLine delay={0.05}>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">{page.overline}</span>
          </MaskedLine>
          <h1 className="mt-6 font-display font-black uppercase tracking-tighter leading-[0.95] text-3xl sm:text-5xl lg:text-6xl max-w-4xl" data-testid="seo-page-title">
            <MaskedLine delay={0.18}>{page.h1}</MaskedLine>
          </h1>
          <FadeIn delay={0.45} className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href="tel:0646300391"
              data-testid="seo-call-button"
              className="btn-sweep inline-flex items-center justify-center gap-3 bg-signal text-white font-display font-bold text-base uppercase tracking-wide px-8 py-4 transition-colors duration-300"
            >
              <Phone size={18} strokeWidth={1.5} />
              Appeler maintenant
            </a>
            <Link
              to={`/${page.slug}#devis`}
              data-testid="seo-quote-button"
              className="btn-sweep btn-sweep-light inline-flex items-center justify-center gap-3 border border-white/40 text-white font-display font-bold text-base uppercase tracking-wide px-8 py-4 transition-colors duration-300"
            >
              Demander un devis gratuit
            </Link>
          </FadeIn>
        </div>
      </section>

      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-6">
            {page.intro.map((p, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <p className="text-base md:text-lg text-midnight/75 leading-relaxed">{linkify(p, usedLinks, currentPath)}</p>
              </FadeIn>
            ))}
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <FadeIn delay={0.2}>
              <Frame
                label="PHOTO — INTERVENTION MSC"
                ratio="aspect-[4/5]"
                src={page.photo}
                alt={page.photoAlt}
              />
            </FadeIn>
          </div>
        </div>
      </section>

      {page.blocks.map((block, i) => (
        <section
          key={block.h2}
          className={`py-20 md:py-28 border-t ${i % 2 === 1 ? "bg-white border-midnight/10" : "bg-bone border-midnight/10"}`}
        >
          <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <span className="font-mono text-sm text-signal tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</span>
              <FadeIn delay={0.05}>
                <h2 className="mt-3 font-display font-black uppercase tracking-tighter leading-tight text-2xl md:text-3xl">
                  {block.h2}
                </h2>
              </FadeIn>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 space-y-5">
              {block.paragraphs.map((p, j) => (
                <FadeIn key={j} delay={j * 0.06}>
                  <p className="text-base text-midnight/70 leading-relaxed">{linkify(p, usedLinks, currentPath)}</p>
                </FadeIn>
              ))}
              {block.list && (
                <FadeIn delay={0.15}>
                  <ul className="mt-4 border-t border-midnight/10">
                    {block.list.map((item) => (
                      <li key={item} className="flex items-center gap-4 py-3 border-b border-midnight/10">
                        <span className="w-1.5 h-1.5 bg-signal shrink-0" />
                        <span className="text-sm text-midnight/80">{item}</span>
                      </li>
                    ))}
                  </ul>
                </FadeIn>
              )}
            </div>
          </div>
        </section>
      ))}

      {page.linksBlock && (
        <section className="bg-white py-16 border-t border-midnight/10">
          <div className="max-w-7xl mx-auto px-5 md:px-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel mb-6">{page.linksBlock.title}</p>
            <div className="flex flex-wrap gap-2" data-testid="seo-links-block">
              {page.linksBlock.links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="font-mono text-[11px] uppercase tracking-[0.08em] text-midnight/70 border border-midnight/10 px-4 py-2 hover:border-signal hover:text-signal transition-colors duration-300"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-signal text-white py-20 grain relative">
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 flex flex-col lg:flex-row lg:items-center gap-10 justify-between">
          <div>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">
              {page.ctaTitle}
            </h2>
            <p className="mt-4 text-white/85 max-w-xl leading-relaxed">
              Déplacement et devis gratuits dans tout le Val-de-Marne. Parlez directement à votre couvreur :
              pas de plateforme intermédiaire.
            </p>
          </div>
          <a
            href="tel:0646300391"
            data-testid="seo-cta-call-button"
            className="btn-sweep inline-flex items-center justify-center gap-3 bg-midnight text-white font-display font-bold text-lg uppercase tracking-wide px-10 py-5 shrink-0 transition-colors duration-300"
          >
            <Phone size={20} strokeWidth={1.5} />
            06 46 30 03 91
          </a>
        </div>
      </section>

      <section id="devis" className="bg-bone py-20 md:py-28 border-b border-midnight/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="mb-14">
            <Overline>Devis gratuit</Overline>
            <h2 className="mt-4 font-display font-black uppercase tracking-tighter leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">
              <MaskedLineScroll>Demandez votre devis gratuit</MaskedLineScroll>
            </h2>
          </div>
          <DevisForm />
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel mb-8">À lire également</p>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-midnight/10">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                data-testid={`seo-related-${p.slug}`}
                className="group border-r border-b border-midnight/10 p-8 flex items-center justify-between gap-4 hover:bg-bone transition-colors duration-300"
              >
                <span className="font-display font-extrabold uppercase tracking-tight text-base">{p.footerLabel}</span>
                <ArrowRight size={18} strokeWidth={1.5} className="text-signal shrink-0 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
